import { useCallback, useEffect, useState } from 'react'

import { useRealtimeFrames, useRealtimeResync } from '@/lib/realtime'
import {
  isInboxNotification,
  isUnread,
  parseNotification,
} from '@/features/notifications/types'

import { discoveryApi } from '../api'

const POLL_INTERVAL_MS = 15_000

/**
 * Header bell badge (UI_PARITY §3.6).
 *
 * Keeps the frozen 15 s visible fallback and also reacts to user-channel
 * `notification` frames, focus and reconnect resync so the count stays current
 * when the socket is available. A badge failure never blocks discovery.
 */
export function useUnreadNotificationCount(): number {
  const [count, setCount] = useState(0)
  const [attempt, setAttempt] = useState(0)

  const refresh = useCallback(() => {
    setAttempt((value) => value + 1)
  }, [])

  useRealtimeFrames((frame) => {
    if (frame.type !== 'notification') return
    const notification = parseNotification(frame.notification)
    if (
      !notification ||
      !isInboxNotification(notification) ||
      !isUnread(notification)
    ) {
      return
    }
    setCount((current) => current + 1)
    refresh()
  })

  useRealtimeResync(refresh)

  useEffect(() => {
    let cancelled = false
    let controller: AbortController | null = null

    async function load() {
      controller?.abort()
      controller = new AbortController()
      try {
        const response = await discoveryApi.unreadNotificationCount(
          controller.signal,
        )
        if (!cancelled) setCount(Math.max(0, response.count))
      } catch {
        // A badge failure must never block discovery.
      }
    }

    void load()
    const timer = window.setInterval(() => void load(), POLL_INTERVAL_MS)
    const onFocus = () => {
      if (document.visibilityState === 'visible') void load()
    }
    document.addEventListener('visibilitychange', onFocus)
    window.addEventListener('focus', onFocus)
    return () => {
      cancelled = true
      controller?.abort()
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onFocus)
      window.removeEventListener('focus', onFocus)
    }
  }, [attempt])

  return count
}
